import React from "react";

const CloudinaryImage = ({
  src,
  alt,
  className = "",
  style = {},
  width,
  height,
  quality = "auto",
  format = "auto",
  crop = "fill",
  gravity = "auto",
  responsive = false,
  webpFallback = true,
  ...props
}) => {
  // Check if URL is from Cloudinary
  const isCloudinaryUrl = (url) => {
    return url && url.includes("res.cloudinary.com");
  };

  // Extract public ID from Cloudinary URL
  const getPublicId = (url) => {
    if (!url) return "";

    // Only process if it's actually a Cloudinary URL
    if (!isCloudinaryUrl(url)) {
      return null; // Not a Cloudinary URL
    }

    // Remove query parameters first
    const cleanUrl = url.split("?")[0];

    // Extract everything after /upload/
    const uploadMatch = cleanUrl.match(/\/upload\/(.+)$/);
    if (!uploadMatch) return null;

    const afterUpload = uploadMatch[1];

    // Split by / to handle version/folders and filename separately
    const parts = afterUpload.split("/");

    // The filename is the last part
    let filename = parts[parts.length - 1];

    // Remove Cloudinary size prefixes from filename only
    const sizePrefixes = ["thumbnail_", "medium_", "large_", "small_"];
    sizePrefixes.forEach((prefix) => {
      if (filename.startsWith(prefix)) {
        filename = filename.replace(prefix, "");
      }
    });

    // Remove file extension for Cloudinary transformations
    filename = filename.replace(/\.[^.]+$/, "");

    // Reconstruct the full public ID with folder structure
    const folderParts = parts.slice(0, -1); // All parts except the last (filename)
    const cleanedParts = [...folderParts, filename];

    return cleanedParts.join("/");
  };

  // Build Cloudinary transformation URL
  const buildCloudinaryUrl = (
    publicId,
    transformations = [],
    outputFormat = "auto"
  ) => {
    if (!publicId) return src;

    const cloudName = process.env.REACT_APP_CLOUDINARY_NAME;
    if (!cloudName) {
      console.warn("REACT_APP_CLOUDINARY_NAME not set in CloudinaryImage");
      return src; // Return original src if no cloud name
    }

    const baseUrl = `https://res.cloudinary.com/${cloudName}/image/upload`;

    const transforms = [`f_${outputFormat}`, ...transformations];

    const transformString = transforms.join(",");

    // Use publicId as-is (no extension needed for transformations)
    return `${baseUrl}/${transformString}/${publicId}`;
  };

  // Generate responsive sizes
  const generateResponsiveSources = (publicId) => {
    const breakpoints = [
      { media: "(max-width: 640px)", width: 640 },
      { media: "(max-width: 1024px)", width: 1024 },
      { media: "(max-width: 1440px)", width: 1440 },
      { media: "(min-width: 1441px)", width: 1920 },
    ];

    return breakpoints.map((bp) => ({
      ...bp,
      webp: buildCloudinaryUrl(publicId, [`w_${bp.width}`], "webp"),
      fallback: buildCloudinaryUrl(publicId, [`w_${bp.width}`], "jpg"),
    }));
  };

  if (!src) return null;

  // If it's not a Cloudinary URL, render as regular img with lazy loading
  if (!isCloudinaryUrl(src)) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading="lazy"
        {...props}
      />
    );
  }

  const publicId = getPublicId(src);

  // If we couldn't extract a public ID, fallback to regular img
  if (!publicId) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        style={style}
        loading="lazy"
        {...props}
      />
    );
  }

  // For responsive images with picture element
  if (responsive) {
    const sources = generateResponsiveSources(publicId);

    return (
      <picture>
        {sources.map((source, index) => (
          <React.Fragment key={index}>
            {webpFallback && (
              <source
                media={source.media}
                srcSet={source.webp}
                type="image/webp"
              />
            )}
            <source media={source.media} srcSet={source.fallback} />
          </React.Fragment>
        ))}
        <img
          src={buildCloudinaryUrl(publicId, [], "jpg")}
          alt={alt}
          className={className}
          style={style}
          loading="lazy"
          {...props}
        />
      </picture>
    );
  }

  // For simple optimized images with WebP fallback
  if (webpFallback) {
    // Build transformations array from props
    const transformations = [];
    if (width) transformations.push(`w_${width}`);
    if (height) transformations.push(`h_${height}`);
    if (crop) transformations.push(`c_${crop}`);
    if (gravity && crop !== "scale") transformations.push(`g_${gravity}`);

    const webpUrl = buildCloudinaryUrl(publicId, transformations, "webp");
    const fallbackUrl = buildCloudinaryUrl(publicId, transformations, "jpg");

    return (
      <picture>
        <source srcSet={webpUrl} type="image/webp" />
        <img
          src={fallbackUrl}
          alt={alt}
          className={className}
          style={style}
          loading="lazy"
          onError={(e) => {
            console.error("❌ CloudinaryImage failed to load:", {
              webpUrl,
              fallbackUrl,
              error: e,
            });
          }}
          {...props}
        />
      </picture>
    );
  }

  // Standard optimized image
  const transformations = [];
  if (width) transformations.push(`w_${width}`);
  if (height) transformations.push(`h_${height}`);
  if (crop) transformations.push(`c_${crop}`);
  if (gravity && crop !== "scale") transformations.push(`g_${gravity}`);

  const optimizedUrl = buildCloudinaryUrl(publicId, transformations, format);


  return (
    <img
      src={optimizedUrl}
      alt={alt}
      className={className}
      style={style}
      loading="lazy"
      onError={(e) => {
        console.error("❌ CloudinaryImage optimized failed to load:", {
          optimizedUrl,
          error: e,
        });
      }}
      {...props}
    />
  );
};

export default CloudinaryImage;
